import { AdditionalBookingSectionEntity, AdditionalBookingOptionEntity } from 'Models/Entities';
import { getFerryAndRouteIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import { compareFnByNumberAsc } from 'Util/_HumanWritten/SortUtils';
import { getAllAdditionalOptionSections } from 'Services/Api/_HumanWritten/AdditionalBookingOptionsService';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { canAdjustRestrictedAddOns } from 'Util/_HumanWritten/addOnPermissions';

interface IAddOnStore {
	addOnSections: AdditionalBookingSectionEntity[];
	retrieveAddOnsAsync(refresh?: boolean, userId?: string | null): Promise<void>;
	getFerriesAndRoutes(): Set<string>;
	getAddOnSectionsByFerriesAndRoutes(ids: string[]): AdditionalBookingSectionEntity[];
	hasPassengerAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean;
	hasVehicleAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean;
	hasFreightAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean;
	findAddOn: (addOnOptionId: string) => AdditionalBookingOptionEntity | null;
	findAddOnSection: (addOnOptionId: string) => AdditionalBookingSectionEntity | null;
}

class AddOnStore implements IAddOnStore {
	public addOnSections: AdditionalBookingSectionEntity[] = [];
	private _fetched: boolean = false;

	/**
	 * This should be called asynchronously to ensure additional booking sections and options are populated.
	 */
	public async retrieveAddOnsAsync(refresh = false, userId: string | null = null) {
		if (refresh) {
			this._fetched = false;
		}
		if (this._fetched) {
			return;
		}

		const addOnSections = await getAllAdditionalOptionSections(userId);

		this.addOnSections = addOnSections.sort(compareFnByNumberAsc(x => x.order));
		this._fetched = true;
	}

	public getFerriesAndRoutes() {
		const set = new Set<string>();
		for (const addOnSection of this.addOnSections) {
			for (const ferry of addOnSection.ferriess) {
				set.add(ferry.ferriesId);
			}
			for (const route of addOnSection.routess) {
				set.add(route.routesId);
			}
		}

		return set;
	}

	public getAddOnSectionsByFerriesAndRoutes(ids: string[]): AdditionalBookingSectionEntity[] {
		const res = this.addOnSections.filter(x => {
			// If no ferries or routes are assigned, the section applies to all
			if (x.ferriess.length === 0 && x.routess.length === 0) {
				return true;
			}

			const ferriesIds = x.ferriess.map(y => y.ferriesId);
			const routesIds = x.routess.map(y => y.routesId);
			const ferriesAndRoutes = ferriesIds.concat(routesIds);

			for (const id of ids) {
				if (ferriesAndRoutes.includes(id)) {
					return true;
				}
			}

			return false;
		});
		return res;
	}

	public hasPassengerAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean {
		const validSectionsBasedOnSelectedTrips = this.getAddOnSectionsByFerriesAndRoutes(getFerryAndRouteIds(trips));

		if (validSectionsBasedOnSelectedTrips === null || validSectionsBasedOnSelectedTrips.length === 0) {
			return false;
		}

		for (const section of validSectionsBasedOnSelectedTrips) {
			if (!section.disabled || section.additionalBookingOptions?.some(o => existingAddOnIds?.includes(o.id))) {
				const options = section.additionalBookingOptions ?? [];
				for (const option of options) {
					if (!option.disabled || existingAddOnIds?.includes(option.id)) {
						if (!option.isRestricted || canAdjustRestrictedAddOns() || existingAddOnIds?.includes(option.id)) {
							if (option.passengerAddOn === true) {
								return true;
							}
						}
					}
				}
			}
		}
		return false;
	}

	public hasVehicleAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean {
		const validSectionsBasedOnSelectedTrips = this.getAddOnSectionsByFerriesAndRoutes(getFerryAndRouteIds(trips));

		if (validSectionsBasedOnSelectedTrips === null || validSectionsBasedOnSelectedTrips.length === 0) {
			return false;
		}

		for (const section of validSectionsBasedOnSelectedTrips) {
			if (!section.disabled || section.additionalBookingOptions?.some(o => existingAddOnIds?.includes(o.id))) {
				const options = section.additionalBookingOptions ?? [];
				for (const option of options) {
					if (!option.disabled || existingAddOnIds?.includes(option.id)) {
						if (!option.isRestricted || canAdjustRestrictedAddOns() || existingAddOnIds?.includes(option.id)) {
							if (option.vehicleAddOn === true) {
								return true;
							}
						}
					}
				}
			}
		}
		return false;
	}

	public hasFreightAddOns(trips: SelectedTrips, existingAddOnIds?: string[]): boolean {
		const validSectionsBasedOnSelectedTrips = this.getAddOnSectionsByFerriesAndRoutes(getFerryAndRouteIds(trips));

		if (validSectionsBasedOnSelectedTrips === null || validSectionsBasedOnSelectedTrips.length === 0) {
			return false;
		}

		for (const section of validSectionsBasedOnSelectedTrips) {
			if (!section.disabled || section.additionalBookingOptions?.some(o => existingAddOnIds?.includes(o.id))) {
				const options = section.additionalBookingOptions ?? [];
				for (const option of options) {
					if (!option.disabled || existingAddOnIds?.includes(option.id)) {
						if (!option.isRestricted || canAdjustRestrictedAddOns() || existingAddOnIds?.includes(option.id)) {
							if (option.freightAddOn === true) {
								return true;
							}
						}
					}
				}
			}
		}
		return false;
	}

	public findAddOn(addOnOptionId: string): AdditionalBookingOptionEntity | null {
		for (const section of this.addOnSections) {
			const addOn = section.additionalBookingOptions.find(x => x.id === addOnOptionId);
			if (addOn) {
				return addOn;
			}
		}
		return null;
	}

	/**
	 * The section an option belongs to, found by walking the sections rather than reading the
	 * option's `additionalBookingSectionId` — the sections endpoint returns options nested under
	 * their section, so the back-reference isn't guaranteed to be populated on the option.
	 *
	 * Route/ferry scoping lives on the SECTION (`RoutesAdditionalBookingSections`), so this is what
	 * "is this add-on offered on that journey" has to be asked of.
	 */
	public findAddOnSection(addOnOptionId: string): AdditionalBookingSectionEntity | null {
		for (const section of this.addOnSections) {
			if (section.additionalBookingOptions.some(x => x.id === addOnOptionId)) {
				return section;
			}
		}
		return null;
	}
}

const addOnStore: IAddOnStore = new AddOnStore();
export default addOnStore;
